import { useEffect, useMemo, useRef, useState } from 'react';
import { useSessionMessagesStore } from '../../../stores/prioritization/sessionMessagesStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { C } from '../theme/participantTokens';

interface SessionChatDrawerProps {
  sessionId: string;
  isOpen: boolean;
  onClose: () => void;
  /** S-3: names are hidden in this session — messages carry no author. */
  hideNames?: boolean;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function msgInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
}

export function SessionChatDrawer({ sessionId, isOpen, onClose, hideNames = false }: SessionChatDrawerProps) {
  const userId = useUserStore(s => s.userId) ?? '';
  const userName = useUserStore(s => s.userName) ?? '';

  const allMessages = useSessionMessagesStore(s => s.messages);
  const messages = useMemo(
    () => allMessages.filter(m => m.sessionId === sessionId).sort((a, b) => +a.createdAt - +b.createdAt),
    [allMessages, sessionId],
  );
  const sendMessage = useSessionMessagesStore(s => s.sendMessage);
  const startPolling = useSessionMessagesStore(s => s.startPolling);
  const stopPolling = useSessionMessagesStore(s => s.stopPolling);
  const isRealtimeConnected = useSessionMessagesStore(s => s.isRealtimeConnected);
  const isSending = useSessionMessagesStore(s => s.isSending);

  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    startPolling(sessionId);
    return () => stopPolling();
  }, [sessionId, startPolling, stopPolling]);

  // Scroll to bottom whenever messages change or drawer opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
    }
  }, [messages, isOpen]);

  // Auto-focus input when drawer opens
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 80);
  }, [isOpen]);

  const handleSend = async () => {
    if (!draft.trim() || isSending) return;
    const workspaceId = getCurrentWorkspaceId() ?? '';
    const ok = await sendMessage({ sessionId, workspaceId, userId, userName, body: draft });
    if (ok) setDraft('');
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <>
      {/* Backdrop (mobile) */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 89, background: 'rgba(0,0,0,0.25)' }}
          aria-hidden
        />
      )}

      {/* Drawer panel */}
      <div
        role="complementary"
        aria-label="Session chat"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 340,
          zIndex: 90,
          display: 'flex',
          flexDirection: 'column',
          background: C.bgRaised,
          borderLeft: `1px solid ${C.rule}`,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.22s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: isOpen ? '-4px 0 24px rgba(0,0,0,0.18)' : 'none',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 14px',
            height: 48,
            borderBottom: `1px solid ${C.rule}`,
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flex: 1 }}>
            <span
              title={isRealtimeConnected ? 'Live' : 'Polling'}
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: isRealtimeConnected ? '#34d399' : C.mustard,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.6rem',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: C.textTer,
              }}
            >
              Session Chat
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: C.textDim,
              fontSize: '1.1rem',
              lineHeight: 1,
              padding: '4px 6px',
            }}
          >
            ×
          </button>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {messages.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                fontSize: '0.78rem',
                color: C.textDim,
                textAlign: 'center',
                padding: '0 24px',
              }}
            >
              No messages yet. Say something to the group.
            </div>
          ) : (
            messages.map(msg => {
              const isMe = msg.userId === userId;
              return (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex',
                    flexDirection: isMe ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: isMe ? C.mustard : C.slate,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: '0.5rem',
                      fontWeight: 700,
                      color: C.bgRaised,
                      flexShrink: 0,
                    }}
                  >
                    {hideNames && !isMe ? '·' : msgInitials(msg.userName || msg.userId)}
                  </div>
                  <div style={{ maxWidth: '72%' }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'baseline',
                        justifyContent: isMe ? 'flex-end' : 'flex-start',
                        marginBottom: 3,
                      }}
                    >
                      {!isMe && (
                        <span style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.68rem', fontWeight: 600, color: C.textSec }}>
                          {hideNames ? 'Someone' : msg.userName || msg.userId.slice(0, 8)}
                        </span>
                      )}
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', color: C.textDim }}>
                        {formatTime(msg.createdAt)}
                      </span>
                    </div>
                    <div
                      style={{
                        background: isMe ? C.mustard : C.surface,
                        color: isMe ? C.bgRaised : C.textPri,
                        border: `1px solid ${isMe ? 'transparent' : C.rule}`,
                        borderRadius: isMe ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                        padding: '7px 11px',
                        fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                        fontSize: '0.82rem',
                        lineHeight: 1.5,
                        wordBreak: 'break-word',
                        opacity: msg.id.startsWith('optimistic-') ? 0.65 : 1,
                      }}
                    >
                      {msg.body}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div
          style={{
            padding: '10px 14px',
            borderTop: `1px solid ${C.rule}`,
            flexShrink: 0,
            display: 'flex',
            gap: 8,
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Message the group…"
            disabled={isSending}
            style={{
              flex: 1,
              height: 36,
              padding: '0 12px',
              borderRadius: 4,
              border: `1px solid ${C.ruleBright}`,
              background: C.surface,
              color: C.textPri,
              fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
              fontSize: '0.82rem',
              outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!draft.trim() || isSending}
            aria-label="Send"
            style={{
              width: 36,
              height: 36,
              borderRadius: 4,
              border: `1px solid ${C.mustard}`,
              background: draft.trim() && !isSending ? C.mustard : 'transparent',
              color: draft.trim() && !isSending ? C.bgRaised : C.textDim,
              cursor: draft.trim() && !isSending ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem',
              transition: 'all 0.15s',
              flexShrink: 0,
            }}
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
}
